import { MixpanelConfig, mixpanelService } from "@/services/mixpanel-service";
import { createContext, ReactNode, useContext, useEffect } from "react";
import { MixpanelEventProperties } from "./mixpanel-events";

interface MixpanelContextType {
  trackPageView: (pageName: string, pagePath: string, additionalProperties?: MixpanelEventProperties) => void;
  identify: (user: { id: number; name: string; admin: boolean }) => void;
  reset: () => void;

  trackFeature: (
    featureName: string,
    actionType: "click" | "view" | "filter" | "search" | "export" | "download",
    additionalProperties?: MixpanelEventProperties,
  ) => void;

  trackFilterChanged: (filterType: string, filterValue: unknown, previousValue?: unknown) => void;
}

const MixpanelContext = createContext<MixpanelContextType | null>(null);

interface MixpanelProviderProps {
  children: ReactNode;
  config: MixpanelConfig;
}

export function MixpanelProvider({ children, config }: MixpanelProviderProps) {
  useEffect(() => {
    mixpanelService.initialize(config);
  }, [config]);

  const contextValue: MixpanelContextType = {
    trackPageView: mixpanelService.trackPageView.bind(mixpanelService),
    identify: mixpanelService.identify.bind(mixpanelService),
    reset: mixpanelService.reset.bind(mixpanelService),

    trackFeature: mixpanelService.trackFeature.bind(mixpanelService),
    trackFilterChanged: mixpanelService.trackFilterChanged.bind(mixpanelService),
  };

  return <MixpanelContext.Provider value={contextValue}>{children}</MixpanelContext.Provider>;
}

export function useMixpanel(): MixpanelContextType {
  const context = useContext(MixpanelContext);
  if (!context) {
    throw new Error("useMixpanel must be used within a MixpanelProvider");
  }
  return context;
}
